import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { ThemeProvider, CssBaseline, Box, Typography } from '@mui/material';
import { theme } from './components/theme/theme';
import Navbar from './components/navigation/navbar';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { listBuildingIds } from './components/handlers/plugCommission-handler';
import {
  DEFAULT_BUILDING_ID,
  resolveDefaultBuildingId,
  selectedBuildingIdAtom,
} from './components/atoms-types/buildingSelection';

const DashboardChargingGrid = lazy(() => import('./components/grids/dashboardChargingGrid'));
const DashboardEnergyGrid = lazy(() => import('./components/grids/dashboardEnergyGrid'));
const SetupGrid = lazy(() => import('./components/grids/setupGrid'));
const PowerGrid = lazy(() => import('./components/grids/powerGrid'));
const ChargerGrid = lazy(() => import('./components/grids/chargerGrid'));
const PlugCommissionGrid = lazy(() => import('./components/grids/plugCommissionGrid'));
const PlugEditGrid = lazy(() => import('./components/grids/plugEditGrid'));
const WifiSwitchGrid = lazy(() => import('./components/grids/WifiSwitchGrid'));
const NucGrid = lazy(() => import('./components/grids/nucGrid'));
const FleetGrid = lazy(() => import('./components/grids/fleetGrid'));
const EventGrid = lazy(() => import('./components/grids/eventGrid'));

type ColorScheme = 'light' | 'dark';

interface ThemeContextType {
  colorScheme: ColorScheme;
  toggleColorScheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  colorScheme: 'dark',
  toggleColorScheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

const APP_TITLE = 'ODP FPM';

const ROUTE_TITLES: Record<string, string> = {
  '/': 'Charging Dashboard',
  '/dashboard/charging': 'Charging Dashboard',
  '/dashboard/energy': 'Energy & Solar Dashboard',
  '/setup': 'Setup',
  '/power': 'Power',
  '/chargers': 'Chargers',
  '/plug-commission': 'Plug Commission',
  '/plugs/commission': 'Plug Commission',
  '/plugs/edit': 'Edit Plugs',
  '/wifi-switch': 'Plug WiFi Switch',
  '/nuc': 'NUC',
  '/fleet': 'Fleet',
  '/events': 'Events',
};

// Fetch this NUC's SiteID once so it can be shown in the browser tab title,
// making bookmarked pages easy to tell apart between FPM sites.
function useSiteId(): string {
  const [siteId, setSiteId] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    fetch(`${window.location.origin}/healthz`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.siteId) setSiteId(String(data.siteId));
      })
      .catch(() => {
        /* leave siteId empty; title falls back to APP_TITLE only */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return siteId;
}

// Building ID this browser should use when a page has no selector of its own.
// Also repairs persisted selections that point at a building that no longer
// exists (e.g. it was renamed from another browser), so this browser falls
// back to a valid building instead of querying an ID with no data.
function useValidBuildingId(): string {
  const { data: buildingIds } = useQuery({ queryKey: ['buildingIds'], queryFn: listBuildingIds });
  const [selected, setSelected] = useAtom(selectedBuildingIdAtom);
  const fallback = buildingIds ? resolveDefaultBuildingId(buildingIds) : null;

  useEffect(() => {
    if (!buildingIds || buildingIds.length === 0) return;
    const known = (id: string) => buildingIds.some((b) => b.toLowerCase() === id.toLowerCase());
    if (selected && !known(selected) && fallback) setSelected(fallback);
  }, [buildingIds, selected, fallback, setSelected]);

  return selected || fallback || DEFAULT_BUILDING_ID;
}

function DocumentTitle({ siteId }: { siteId: string }) {
  const location = useLocation();
  useEffect(() => {
    const page = ROUTE_TITLES[location.pathname];
    const parts = [page, siteId, APP_TITLE].filter(Boolean);
    document.title = parts.join(' · ');
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, siteId]);
  return null;
}

function App() {
  // Fallback BuildingID passed to pages. The power/chargers/setup pages
  // override this with the shared, persisted selection; the Events page, which
  // has no selector, uses it directly.
  const buildingID = useValidBuildingId();
  const siteId = useSiteId();
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    // Check localStorage for saved preference, default to 'dark'
    const saved = localStorage.getItem('colorScheme') as ColorScheme;
    return saved || 'dark';
  });

  useEffect(() => {
    // Update the document element with the color scheme attribute
    document.documentElement.setAttribute('data-toolpad-color-scheme', colorScheme);
    localStorage.setItem('colorScheme', colorScheme);
  }, [colorScheme]);

  const toggleColorScheme = () => {
    setColorScheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ colorScheme, toggleColorScheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Router>
            <DocumentTitle siteId={siteId} />
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Navbar />
              <Box sx={{ flex: '1 1 auto' }}>
                <Suspense fallback={<div style={{ padding: 16 }}>Loading...</div>}>
                  <Routes>
                    {/* Home page is the Charging dashboard. */}
                    <Route path="/" element={<DashboardChargingGrid />} />
                    <Route path="/dashboard" element={<Navigate to="/" replace />} />
                    <Route path="/dashboard/charging" element={<Navigate to="/" replace />} />
                    <Route path="/dashboard/energy" element={<DashboardEnergyGrid />} />
                    <Route path="/setup" element={<SetupGrid BuildingID={buildingID} />} />
                    <Route path="/power" element={<PowerGrid BuildingID={buildingID} />} />
                    <Route path="/chargers" element={<ChargerGrid BuildingID={buildingID} />} />
                    <Route path="/plugs/commission" element={<PlugCommissionGrid BuildingID={buildingID} />} />
                    <Route path="/plugs/edit" element={<PlugEditGrid />} />
                    {/* Back-compat: old bookmarks land on the new Plugs -> Commission route. */}
                    <Route path="/plug-commission" element={<Navigate to="/plugs/commission" replace />} />
                    <Route path="/wifi-switch" element={<WifiSwitchGrid />} />
                    <Route path="/nuc" element={<NucGrid />} />
                    <Route path="/fleet" element={<FleetGrid />} />
                    <Route path="/events" element={<EventGrid BuildingID={buildingID} />} />
                  </Routes>
                </Suspense>
              </Box>
              <Box
                component="footer"
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  py: 1,
                  px: 2,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Omni Directional Power copyright 2026 | software version 1.0 (build {String(process.env.REACT_APP_BUILD_NUMBER ?? 'dev').padStart(3, '0')})
                </Typography>
              </Box>
            </Box>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
